import { apiClient } from './apiClient';
import type { ApiResponse } from '../types/api';

export interface DashboardStats {
  // For regular users
  myProfiles?: number;
  activeProfiles?: number;
  myTransactions?: number;
  successRate?: string;
  pendingTransactions?: number;
  
  // For developers
  totalUsers?: number;
  totalProfiles?: number;
  totalTransactions?: number;
  totalVolume?: number;
}

export interface RecentActivity {
  _id: string;
  transaction: string;
  amount: number;
  statusTxt: string;
  createdAt: string;
  profileId: {
    _id: string;
    accname: string;
    accbank: string;
  };
  clientId?: string;
  remark?: string;
}

class DashboardService {
  /**
   * Get dashboard statistics for the current user
   */
  async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    try {
      const response = await apiClient.get<DashboardStats>('/dashboard/stats');
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get recent activity for the dashboard
   */
  async getRecentActivity(limit: number = 10): Promise<ApiResponse<RecentActivity[]>> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('limit', limit.toString());
      
      const response = await apiClient.get<RecentActivity[]>(
        `/dashboard/activity?${queryParams.toString()}`
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Format currency amount
   */
  formatCurrency(amount: number, currency: string = 'MYR'): string {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2
    }).format(amount);
  }

  /**
   * Format large numbers with commas
   */
  formatNumber(num: number): string {
    return new Intl.NumberFormat('en-US').format(num);
  }

  /**
   * Get status color for transaction status
   */
  getStatusColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'success':
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
      case 'waiting':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
      case 'error':
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  /**
   * Get transaction type icon
   */
  getTransactionIcon(type: string): string {
    switch (type.toLowerCase()) {
      case 'transfer':
        return '📤';
      case 'receive':
      case 'receiving':
        return '📥';
      case 'payment':
        return '💳';
      default:
        return '💰';
    }
  }

  /**
   * Calculate percentage change
   */
  calculatePercentageChange(current: number, previous: number): string {
    if (previous === 0) return current > 0 ? '+100%' : '0%';
    const change = ((current - previous) / previous) * 100;
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(1)}%`;
  }

  /**
   * Get time ago string
   */
  getTimeAgo(date: string): string {
    const now = new Date();
    const past = new Date(date);
    const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return past.toLocaleDateString();
  }
}

export const dashboardService = new DashboardService();
